import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { getNavigatorLanguageOrFallback } from 'internationalization/i18n';
import InlineForm from 'components/forms/InlineForm';
import { FieldType } from 'types/FieldTypes';
import { FormButtonType } from 'types/ButtonTypes';
import { v4 as uuidv4 } from 'uuid';
import { saveData } from 'services/api/saveData';
import { convertLocalDateTimeToUTC } from 'internationalization/timezoneConversions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightLong } from '@fortawesome/free-solid-svg-icons';
import { setCsrfToken } from 'services/authentication/csrfFunctions';

declare global {
	interface Window {
		grecaptcha: {
			execute: (siteKey: string, options: { action: string }) => Promise<string>;
		};
	}
}

const RECAPTCHA_SITE_KEY = "6LfHT4csAAAAACpFukq_aIK7XYs4jw2TDr8-yB8S";

const CreateTrialForm: React.FC = () => {
    const { handleLogin } = useAuthContext();
    const viewKey = useMemo(() => uuidv4(), []);
    const signupStartedAt = Date.now();

    // Set a csrf token in the browser
    useEffect(() => {
        setCsrfToken();
    }, []);
    
    // Handle submit
    const handleTrialSubmitAndLogin = async (submitData: Record<string, any>) => {
        try {

            // Get recaptcha token (v3)
            const recaptchaToken = await window.grecaptcha.execute(
                RECAPTCHA_SITE_KEY,
                { action: "create_trial" }
            );

            // Get the navigator language of the requester
            const navigator_language = getNavigatorLanguageOrFallback(navigator.language, 'language');

            // Create the trial in the backend
            const response = await saveData({ 
                apiUrl: 'post_trial', 
                method: 'post', 
                data: { 
                    ...submitData, 
                    navigator_language,
                    recaptcha: recaptchaToken,
                    signup_started_at: signupStartedAt
                } 
            });

            // Handle login after the trial is created
            if (response && response.status === 201) {
                const { email, password } = submitData;

                // Get the navigator locale and timezone
                const navigator_locale = getNavigatorLanguageOrFallback(navigator.language, 'locale');
                const navigator_timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

                // Get the current datetime and convert to utc (the backend only handles utc)
                const currentDateTime = new Date();
                const utc_datetime = convertLocalDateTimeToUTC(currentDateTime, navigator_locale, navigator_timezone);

                // Set remember login to false
                const remember_login = false;

                await handleLogin(
                    email, 
                    password, 
                    navigator_language, 
                    navigator_locale, 
                    navigator_timezone, 
                    utc_datetime,
                    remember_login
                );
            } else {
                throw new Error('validation.trial.creation_failed');
            }

        } catch (error) {
            console.error("Trial creation failed:", error);
            throw error;
        }
    };

    const fields: FieldType[] = [
        {
            type: 'text',
            name: 'company_name',
            placeholder: 'account.company_name_placeholder',
            honeypotField: true,
        },
        {
            type: 'text',
            name: 'first_name',
            label: 'account.first_name_label',
            placeholder: 'account.first_name_placeholder',
            width: '50%',
            group: 'field-row'
        },
        {
            type: 'text',
            name: 'last_name',
            label: 'account.last_name_label',
            placeholder: 'account.last_name_placeholder',
            width: '50%',
            group: 'field-row'
        },
        {
            type: 'email',
            name: 'email',
            label: 'account.email_label',
            placeholder: 'account.email_placeholder',
        },
        {
            type: 'password',
            name: 'password',
            label: 'account.password_label',
            placeholder: 'account.password_placeholder',
        },
        
    ];

    const buttons: FormButtonType[] = [
        {
            label: 'account.create_account_button_label',
            position: 'fullwidth',
            hierarchy: 'primary',
            onClick: 'handleSubmit',
            size: 'large',
            showLoader: true,
            icon: <FontAwesomeIcon icon={faArrowRightLong} />
        },
    ];

    return (
        <>
            <Helmet>
                <script 
                    src={`https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`} 
                    async 
                    defer 
                />
            </Helmet>

            <InlineForm
                viewKey={viewKey}
                customApiFunction={handleTrialSubmitAndLogin}
                fields={fields}
                buttons={buttons}
            />
        </>
    );
};

export default CreateTrialForm;